// TEMPORARY web38_hsa_fsa_demand experiment (Narek, Sep 9 2026): one demand-test
// question asking whether the user has an HSA/FSA account and would want to
// pay with it. US-only enrollment is enforced in Statsig (targeting gate
// country_us), not here. Teardown: delete this folder and its call sites in
// App.tsx, types.ts, and debugExperiment.ts.

export const WEB38_HSA_EXPERIMENT_NAME = "web38_hsa_fsa_demand";
export const WEB38_HSA_ARM_VARIATION = "variation";
export const WEB38_HSA_ARM_CONTROL = "control";
export const WEB38_HSA_QUESTION_NAME = "hsaFsa";

// Answer values separate account ownership from payment intent ("yes_pay" vs
// "yes_other") and measure the education gap ("unsure") — the doc's two-signal
// design. They are logged verbatim as hsa_question_answered.hsa_answer.
// Title/description go through the survey's markdown→HTML pass (highlight
// span, bold); the description is styled as the mock's green callout in
// App.scss ([data-name="hsaFsa"]). Plain-text choices (no emoji, per Steven).
const hsaQuestion = {
  name: WEB38_HSA_QUESTION_NAME,
  title: 'Do you have an <span class="highlight">HSA or FSA</span> account?',
  description:
    "These accounts hold **pre-tax money**, so eligible health purchases can cost up to **~30% less**. Some fitness programs qualify.",
  type: "radiogroup",
  choices: [
    { value: "yes_pay", text: "Yes, and I'd want to pay with it" },
    { value: "yes_other", text: "Yes, but I'd probably pay another way" },
    { value: "no", text: "No" },
    { value: "unsure", text: "I'm not sure what that is" },
  ],
  isRequired: true,
};

type QuizPage = {
  name: string;
  elements?: readonly unknown[];
  [key: string]: unknown;
};

const hsaPage: QuizPage = {
  name: WEB38_HSA_QUESTION_NAME,
  elements: [hsaQuestion],
};

// Inserted AFTER focus (the last base-quiz question) so it is the final screen
// before the loading bars, per the doc. Because focus is no longer the last
// question in this arm, its "Last question {name}!" lead-in is dropped there.
const ANCHOR_PAGE = "focus";
const LAST_QUESTION_PREFIX = /^Last question\s*/;

export function buildHsaQuestionPages<T extends QuizPage>(
  pages: readonly T[],
  enabled: boolean
): readonly T[] {
  if (!enabled) return pages;
  if (pages.some((p) => p.name === hsaPage.name)) return pages;
  const i = pages.findIndex((p) => p.name === ANCHOR_PAGE);
  // Anchor missing (unexpected base-quiz change): fail open with no insert
  // rather than appending the question somewhere surprising.
  if (i === -1) return pages;
  const next = pages.slice() as T[];
  const focus = next[i] as QuizPage;
  next[i] = {
    ...focus,
    elements: (focus.elements ?? []).map((el) => {
      const q = el as { name?: string; title?: string | { default: string; es?: string } };
      if (q.name !== ANCHOR_PAGE) return el;
      if (typeof q.title === "string") return { ...q, title: q.title.replace(LAST_QUESTION_PREFIX, "") };
      // Localized title (Spanish quiz): drop the English lead-in; the Spanish
      // text keeps its own (this arm is decided Control, forced-only).
      if (q.title && typeof q.title.default === "string") {
        return { ...q, title: { ...q.title, default: q.title.default.replace(LAST_QUESTION_PREFIX, "") } };
      }
      return el;
    }),
  } as unknown as T;
  next.splice(i + 1, 0, hsaPage as unknown as T);
  return next;
}
